import ChannelFavorite from '@@types/ChannelFavorite';
import { CHANNEL_ID, FOLDER_ID, StringTMap } from '@@types/common';
import Favorite from '@@types/Favorite';
import FolderFavorite from '@@types/FolderFavorite';

import { FAVORITE_TYPE } from '~/constants';
import { lift, mMaybe } from '~/utils/monadUtil';
import { action, computed, observable } from 'mobx';
import {
  append,
  complement,
  contains,
  find,
  findIndex,
  flatten,
  identity,
  isNil,
  map,
  mergeRight,
  pipe,
  pluck,
  prop,
  propEq,
  reject,
} from 'ramda';
import { Maybe } from 'ramda-fantasy';

class FavoritesStore {
  constructor() {}

  @observable
  folders: FolderFavorite[] = [];

  @observable
  openedMap: StringTMap<boolean> = {};

  @computed
  get channels(): ChannelFavorite[] {
    return pipe<any, any, any>(pluck('channels'), flatten)(this.folders);
  }

  @computed
  get rootChannels(): ChannelFavorite[] {
    return pipe<any, any, any>(this.safeGetFolderById, mMaybe([], prop('channels')))('defaultFolder');
  }

  createDefaultFolder = (channels): FolderFavorite => {
    return {
      channels,
      id: 'defaultFolder',
      title: '',
      type: FAVORITE_TYPE.FOLDER,
    };
  };

  safeGetChannelById = (id: CHANNEL_ID): Maybe<ChannelFavorite> => {
    return pipe<any, any, any>(find(propEq('id', id)), lift(identity))(this.channels);
  };

  safeGetChannelByChannelId = (channelId: CHANNEL_ID): Maybe<ChannelFavorite> => {
    return pipe<any, any, any>(find(propEq('channelId', channelId)), lift(identity))(this.channels);
  };

  safeGetFolderById = (folderId: FOLDER_ID): Maybe<FolderFavorite> => {
    return pipe<any, any, any>(find(propEq('id', folderId)), lift(identity))(this.folders);
  };

  getFolderByChannelFavoriteId = (channelFavoriteId: FOLDER_ID): Maybe<FolderFavorite> => {
    return pipe<any, any, any>(
      find((folder: FolderFavorite) => {
        return contains(channelFavoriteId, pluck('id', folder.channels));
      }),
      lift(identity)
    )(this.folders);
  };

  getFolderByChannelId = (channelId: CHANNEL_ID): Maybe<FolderFavorite> => {
    return pipe<any, any, any>(
      find((folder: FolderFavorite) => contains(channelId, pluck('channelId', folder.channels))),
      lift(identity)
    )(this.folders);
  };

  getFolderByTitle = (title: string): Maybe<FolderFavorite> => {
    return pipe<any, any, any>(find(propEq('title', title)), lift(identity))(this.folders);
  };

  isExistTitle = (title: string): boolean => {
    return pipe<any, any, any>(this.getFolderByTitle, Maybe.isJust)(title);
  };

  isFavoriteChannel = (channelId: CHANNEL_ID): boolean => {
    return pipe<any, any, any>(this.safeGetChannelByChannelId, Maybe.isJust)(channelId);
  };

  isRootFavoriteByChannelId = (channelId: CHANNEL_ID): boolean => {
    return pipe(find(propEq('channelId', channelId)), complement(isNil))(this.rootChannels);
  };

  @action
  addChannel = (channel: ChannelFavorite): void => {
    pipe<any, any, any>(
      this.safeGetFolderById,
      map((folder: FolderFavorite) => folder.channels.unshift(channel))
    )('defaultFolder');
  };

  @action
  addChannelToFolder = (channel: ChannelFavorite, folderId: FOLDER_ID): void => {
    pipe<string, any, any>(
      this.safeGetFolderById,
      map((folder: FolderFavorite) => (folder.channels = append(channel, folder.channels)))
    )(folderId);
  };

  @action
  addFavorite = (favorite: Favorite): void => {
    favorite.type === FAVORITE_TYPE.FOLDER ? this.addFolder(favorite as FolderFavorite) : this.addChannel(favorite);
  };

  @action
  addFolder = (folder: FolderFavorite): void => {
    this.folders.unshift(folder);
  };

  @action
  setFolderChannels = (channels: ChannelFavorite[], folderId: FOLDER_ID): void => {
    pipe<any, any, any>(
      this.safeGetFolderById,
      map((folder: FolderFavorite) => (folder.channels = channels))
    )(folderId);
  };

  @action
  setFavorites = (favorites?: { channels: ChannelFavorite[]; folders: FolderFavorite[] }): void => {
    const { channels = [], folders = [] } = favorites ?? {};
    this.folders = [...folders, this.createDefaultFolder(channels)] as FolderFavorite[];
  };

  @action
  setFolders = (folders: FolderFavorite[]): void => {
    this.folders = folders;
  };

  @action
  setOpenedFlag = (folderId: FOLDER_ID, value: boolean): void => {
    this.openedMap[folderId] = value;
  };

  @action
  setOpenedFlagMap = (map: StringTMap<boolean>): void => {
    this.openedMap = map;
  };

  @action
  removeChannel = (favorite: Favorite): void => {
    pipe<any, any, any>(
      this.getFolderByChannelFavoriteId,
      map((folder: FolderFavorite) => {
        folder.channels = reject(propEq('id', favorite.id), folder.channels) as ChannelFavorite[];
      })
    )(favorite.id);
  };

  @action
  updateFolder = (folderId: FOLDER_ID, folder: FolderFavorite): void => {
    const index = findIndex(propEq('id', folderId), this.folders);
    if (index === -1) {
      return this.addFolder(folder);
    }

    this.folders[index] = mergeRight(this.folders[index], folder);
  };
}

export default FavoritesStore;

export const favoritesStore = new FavoritesStore();
